import React, { FC, memo, useState } from 'react';
import { CollapseHeader, CollapsePanel, CollapseIcon } from './collapse/collapse.component';
import styles from './accordion.module.css';

type AccordionItem = {
    id: string; // Unique identifier for the item
    title: string;
    content: React.ReactNode;
    initAsOpen?: boolean; // Optional prop to set initial open state
};

type Props = {
    items?: AccordionItem[];
    multiple?: boolean; // Optional prop to allow multiple items to be open at once
};

const Accordion: FC<Props> = memo((props) => {
    const initialOpened =
        props.items?.filter(({ initAsOpen }) => initAsOpen).map(({ id }) => id) || [];

    const [selected, setSelected] = useState<string[]>(initialOpened);

    if (!props.items || props.items.length === 0) {
        return null; // Return null if there are no items to display
    }

    const { items, multiple } = props;

    const handleClick = (id: string) => {
        if (multiple) {
            setSelected((prev) =>
                prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
            );
        } else {
            setSelected((prev) => (prev.includes(id) ? [] : [id]));
        }
    };

    return (
        <div>
            {items.map(({ id, title, content }, i) => {
                const isOpen = selected.includes(id);
                return (
                    <div key={`${id}-${i}`} id={`'accordion-${id}`} className={styles.item}>
                        <CollapseHeader
                            className={styles.trigger}
                            isOpen={isOpen}
                            onClick={() => handleClick(id)}
                            textVariant='display4'
                        >
                            {title}
                        </CollapseHeader>
                        <CollapsePanel isOpen={isOpen}>
                            <div className={styles.content}>{content}</div>
                        </CollapsePanel>
                    </div>
                );
            })}
        </div>
    );
});

Accordion.displayName = 'Accordion';
export { Accordion, CollapseHeader, CollapsePanel, CollapseIcon };
